import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ContinueRouteChangeType, RouteChangeType } from "abreact/incubator/ui/app/router/RouteChangeContext";
import { PopupRegistryImpl } from "abreact/incubator/ui/utils/PopupRegistryImpl";
import { ProgressManager } from "abreact/progress/manager/ProgressManager";
import { AbAlert } from "absdk/commons/ui/popup/AbAlert";
import { AbWebServices } from "abweb/service/AbWebServices";
import { DestroyListenerMgr } from "abweb/service/destroyListener/DestroyListenerMgr";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOG = LogUtils.getLogger(__filename);
/**
 * A RouteHandler is a construct that sits at the intersection of
 * the currently displayed view and the routes associated with it.
 * It launches the view-specific component with the appropriate props (machine).
 *
 * <p>Note that recommended design pattern for each application to have a <MyApp>RouteHandler that is the parent
 * route handler for all concrete route handlers in the application.  This support adding application-specific logic
 * and workflow support that will be shared by all application route handlers.
 */
export class AbstractRouteHandler {
  /**
   * @param {string[]} routeTemplates Full set of routes that the RouteHandler might need to reason about
   * (including populating route params).  It registers the set of RouteTemplates that it supports to help reason
   * about major route changes.
   * @param _currentRouteTitle If the current route title (shows in browser tab and browser history) is constant
   * (no runtime parameter values) then pass it in via the constructor (taking care to localize it first).
   * However, if the route template has parameters, then override the getCurrentRouteTitle method instead.
   */
  constructor(routeTemplates, _currentRouteTitle = null) {
    _defineProperty(this, "destroyListenerMgr", new DestroyListenerMgr());
    // Popup registry is backed by destroy listeners,
    // and destroyListeners are torn down in reverse registration order on major route change (unmountMajorRoute)
    _defineProperty(this, "_popupRegistry", new PopupRegistryImpl(this.destroyListenerMgr));
    _defineProperty(this, "routeTemplatesSet", void 0);
    this.routeTemplates = routeTemplates;
    this._currentRouteTitle = _currentRouteTitle;
    this.routeTemplatesSet = new Set(this.routeTemplates);
  }

  /**
   * On 'Mid' route changes, want to transfer route handler configuration from old RH to new RH to that
   * existing view component initiated registrations (popups, pfs, destroy listeners) are not 'lost'
   * when new route handler is instantiated.
   */
  updateFromOldRouteHandler(oldRH) {
    // Need to copy verbatim (same backing instance) as open popups have callbacks registered to invoke
    // methods on each when closed (see PopupRegistryImpl::registerPopup implementation).
    // destroyListenerMgr is backing store for popups, pfs, and externally registered (by View) destroy listeners
    this.destroyListenerMgr = oldRH.destroyListenerMgr;
    this._popupRegistry = oldRH._popupRegistry;
  }

  /**
   * @inheritDoc
   */
  get popupRegistry() {
    return this._popupRegistry;
  }

  ///////////// Core Lifecycle Methods /////////////

  /**
   * @inheritDoc
   */

  /**
   * @inheritDoc
   */

  // Every subclass needs to implement their own rendering method

  /**
   * @inheritDoc
   */
  unmountMajorRoute(routeChangeType = RouteChangeType.MAJOR) {
    // also destroys any open popups if we are doing a major route change
    ProgressManager.singleton.clearBeforeRouteChange();
    return this.destroyListenerMgr.destroyAll();
  }
  getRouteChangeType(newRoute) {
    return this.isMajorRouteChange(newRoute) ? RouteChangeType.MAJOR : RouteChangeType.MINOR;
  }

  /**
   * @return {boolean} true iff the new route is fundamentally different than existing route.
   * Defaults to return false when the newRouteTemplate is also supported by the current route handler,
   * and with the same identity (see {@link isSameIdentityParamValues})
   * <p>The major/minor change designation will determine:
   *   - Whether the current view's 'unmountMajorRoute()' method is invoked before navigating to the new view
   *   - Whether the user is prompted for Save/Discard/Cancel on Browser-initiated route changes
   *      when the current route handler hasUnsavedChanges.
   */
  isMajorRouteChange(newRoute) {
    // Default minor route change strategy: it is a minor change iff both route templates are backed
    // by the same route handler, AND they have the same identity params (e.g., docId or rulesetId).
    const isMinorRouteChange = this.routeTemplatesSet.has(newRoute.routeTemplate) && this.isSameIdentityParamValues(newRoute);
    // Everything else is a major route change.
    return !isMinorRouteChange;
  }

  /**
   * Return whether the new route is for the 'same' document (aka identity) as for the current route.
   * Default assumes all params are non-identity params, so it returns true.
   * RouteHandler classes should override to reason about 'identity' params specific to the RouteHandler,
   * such as ruleset 'A' vs ruleset 'B'.
   * @param {string} newRoute
   * @return {boolean} true if the 'identity' of the backing document for current route handler
   * won't change when transition to new route
   * (e.g., newRouteParams.rulesetRpath is equal to this.ruleset_machine.ruleset.rpath)
   */
  isSameIdentityParamValues(newRoute) {
    return true;
  }

  ///////////// Route Reasoning /////////////

  /**
   * @inheritDoc
   */

  /**
   * @inheritDoc
   */
  getCurrentRouteTitle() {
    return this._currentRouteTitle;
  }

  ///////////// Support ability to Veto Browser-initiated Route Change /////////////

  /**
   * @inheritDoc
   */
  isAuthorized(newRouteProps) {
    return true;
  }

  /**
   * @inheritDoc
   */
  continueRouteChange(routeChangeContext) {
    if (AbstractRouteHandler.vetoDialogBeingDisplayed) {
      // Do NOT let them navigate away (even via Browser back button!) while a Save/Discard/Cancel dialog is already showing...
      return ContinueRouteChangeType.CANCEL;
    }
    // TODO directly add below vetoBrowserRouteChange fn logic once all RH overrides of it are removed
    // (only overrides are in EI legacy routes...)
    // return !this.vetoBrowserRouteChange(routeChangeContext.newRouteState, routeChangeContext.routeChangeType === RouteChangeType.MAJOR);
    return this.vetoBrowserRouteChange(routeChangeContext.newReactRoute, routeChangeContext.routeChangeType === RouteChangeType.MAJOR) ? ContinueRouteChangeType.CANCEL_AND_RETRY : ContinueRouteChangeType.CONTINUE;
  }

  /**
   * Implements default veto strategy of vetoing route change if it is a major route change AND there are unsaved changes.
   * Override to implement a different strategy (or just override the 'hasUnsavedChanges' method).
   * Returning true will cause retryRouteChange to be invoked.
   * @param newRoute
   * @param isMajorRouteChange
   */
  vetoBrowserRouteChange(newRoute, isMajorRouteChange) {
    return isMajorRouteChange && this.hasUnsavedChanges();
  }

  /**
   * @inheritDoc
   */
  hasUnsavedChanges() {
    return false;
  }

  /** @inheritDoc */
  async retryRouteChange(routeChangeContext) {
    // Defensive check (should never happen)
    if (AbstractRouteHandler.vetoDialogBeingDisplayed) {
      LOG.error("Should not be able to get to retryRouteChange if veto dialog already being displayed for this route.");
      return false;
    }
    AbstractRouteHandler.vetoDialogBeingDisplayed = true;
    let continueToNewRoute = false;
    try {
      // If it does throw, the error will be displayed and logged by the 'last chance' error infrastructure
      continueToNewRoute = await this.displayVetoRouteChangeDlg();
      // Defensive: If continueToNewRoute is true, then current route must not have any unsaved changes
      if (continueToNewRoute && this.hasUnsavedChanges()) {
        // Could log and return false, but
        // this represents a fundamental contract issue that developer needs to fix so throw
        throw new Error(`Veto dialog returned true implying changes are saved or discarded, but route '${this.getCurrentRoute().routeTemplate}' still has unsaved changes.`);
      }
    } finally {
      AbstractRouteHandler.vetoDialogBeingDisplayed = false;
    }
    return continueToNewRoute;
  }

  /**
   * <p>This method is invoked on a separate 'thread' if navigating to the newRoute was vetoed.
   * It provides a default dialog just says that the current view has unsaved changes,
   * so the new route change was vetoed.
   * Override to provide a view-specific dialog (frequently with save/discard/cancel semantics),
   * and return true iff want to retry changing to the newRoute.
   *
   * <p>The provided SaveDiscardDialog:displaySaveCancelDiscardDialog method is a useful dialog that you can directly
   * use in an override of this method - it generates a customizable dialog with the appropriate Save, Discard, and Cancel
   * buttons, and the developer provides onSave and onDiscard callbacks to perform the corresponding saving or discarding
   * of the changes.
   *
   * @return true if want to retry navigating to newRoute, false otherwise
   */
  async displayVetoRouteChangeDlg()
  // TODO - replace boolean with RouteChangeVetoStatus enum to make bugs easier to spot
  {
    await AbAlert.showError(AbReactResources.abReactTopDown_leaveChangedPageAlert(), AbReactResources.abReactTopDown_leaveChangedPageAlert_title()).untilClosed;
    return false;
  }

  ///// Utilities ///////////////////

  /**
   * @deprecated Use react component 'onUnmount' hook to register unmount callbacks.
   * Will be removed once all apps are using the AbSDK Router.
   */
  registerDestroyListener(listener) {
    return this.destroyListenerMgr.registerDestroyListener(listener);
  }

  /**
   * @deprecated Use react component 'onMount' hook to register pfs hooks, and auto-teardown on
   * react component unmount.  Will be removed once all apps are using the AbSDK Router.
   *
   * Register route-specific pfs content callbacks, which will be torn down when the RouteHandler is unloaded.
   * Take care not to register duplicates on minor route changes (see routeChangeContext.isMajorRouteChange)
   * @param pfsParamCallbacks
   * @param pfsFileCallbacks
   */
  registerPFSCallbacks(pfsParamCallbacks, pfsFileCallbacks = []) {
    const pfsService = AbWebServices.packageForSupportService;
    pfsParamCallbacks.forEach(paramGenFn => this.registerDestroyListener(pfsService.addParameterGenerator(paramGenFn)));
    pfsFileCallbacks.forEach(fileContentGen => this.registerDestroyListener(pfsService.addFileGenerator(fileContentGen)));
  }
}
// Indicates whether veto dialog is currently (already) being displayed.
// Might be better off as an application-level machine prop (or nav service prop)?
// Or hook into LayerManager machinery to determine if vetoDialog is already being displayed (or
// how about if any other dialog is being displayed?)
_defineProperty(AbstractRouteHandler, "vetoDialogBeingDisplayed", false);
export let RouteChangeVetoStatus = /*#__PURE__*/function (RouteChangeVetoStatus) {
  RouteChangeVetoStatus["CONTINUE_TO_NEW_ROUTE"] = "CONTINUE_TO_NEW_ROUTE";
  RouteChangeVetoStatus["STAY_AT_CURRENT_ROUTE"] = "STAY_AT_CURRENT_ROUTE";
  return RouteChangeVetoStatus;
}({});
//# sourceMappingURL=AbstractRouteHandler.js.map
